import React, { useEffect } from "react";
import { Grid, Box, styled } from "@mui/material";
import { List, PersonOutline, PeopleOutline, Subject, SvgIconComponent, TimerOutlined } from "@mui/icons-material";
import { H1, Caption, colorsLight, Card } from "@coherehealth/common";
import {
  QueueViewState,
  useGetIncompleteQueueVolume,
  useGetUserQPHForQueue,
  useGetQueueWorkedCount,
} from "@coherehealth/qm-api";

import { showQphConfig } from "../../util/QueueUtil";
import { useSnackbar } from "notistack";

interface Props {
  queueView: QueueViewState;
  activeQueueId?: string;
}

const DashboardMetrics = ({ queueView, activeQueueId }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    data: queueVolume,
    error: queueVolumeError,
    refetch: getIncompleteQueueVolume,
  } = useGetIncompleteQueueVolume({
    lazy: true,
    queueId: activeQueueId || "",
  });
  const {
    data: todayWork,
    error: todayWorkError,
    refetch: getQueueWorkedCount,
  } = useGetQueueWorkedCount({
    lazy: true,
    queueId: activeQueueId || "",
  });

  const {
    data: qph,
    error: qphError,
    refetch: getUserQPH,
  } = useGetUserQPHForQueue({ lazy: true, queueId: activeQueueId || "" });

  const showQPH = showQphConfig(queueView);

  useEffect(() => {
    if (activeQueueId) {
      getIncompleteQueueVolume();
      getQueueWorkedCount();
    }
  }, [activeQueueId, getIncompleteQueueVolume, getQueueWorkedCount]);

  useEffect(() => {
    if (activeQueueId) {
      getUserQPH();
    }
  }, [getUserQPH, activeQueueId]);

  useEffect(() => {
    if (queueVolumeError) {
      enqueueSnackbar(`Failed to load queue volume: ${queueVolumeError.message}`, { variant: "error" });
    }

    if (todayWorkError) {
      enqueueSnackbar(`Failed to load your work: ${todayWorkError?.message}`, { variant: "error" });
    }

    if (qphError) {
      enqueueSnackbar(`Failed to load your QPH: ${qphError?.message}`, { variant: "error" });
    }
  }, [enqueueSnackbar, queueVolumeError, todayWorkError, qphError]);

  return (
    <Grid container sx={{ flexWrap: "nowrap" }}>
      <Box mr={8}>
        <QueueVolumeMetric queueVolumeCount={queueVolume?.count || 0} />
      </Box>
      <Box mr={8}>
        <AgentWorkedMetric agentWorkedCount={todayWork?.count || 0} />
      </Box>
      {showQPH && (
        <Box mr={8}>
          <QPHMetric qphValue={qph?.count} />
        </Box>
      )}
    </Grid>
  );
};

export const QueueVolumeMetric = ({
  queueVolumeCount,
  metricTitle,
}: {
  queueVolumeCount?: number;
  metricTitle?: string;
}) => {
  return (
    <MetricDisplay Icon={List} metricTitle={metricTitle || "Queue volume"} metricValue={queueVolumeCount || "- -"} />
  );
};

export const AllQueueVolumeMetric = ({ queueVolumeCount }: { queueVolumeCount?: number }) => {
  return <MetricDisplay Icon={Subject} metricTitle="Queue volume (All)" metricValue={queueVolumeCount || "- -"} />;
};

export const AgentWorkedMetric = ({ agentWorkedCount }: { agentWorkedCount?: number }) => {
  return <MetricDisplay Icon={PersonOutline} metricTitle="You worked" metricValue={agentWorkedCount || "- -"} />;
};

export const TeamWorkedMetric = ({ teamWorkedCount }: { teamWorkedCount?: number }) => {
  return <MetricDisplay Icon={PeopleOutline} metricTitle="Team worked" metricValue={teamWorkedCount || "- -"} />;
};

const QPHMetric = ({ qphValue }: { qphValue?: number }) => {
  return <MetricDisplay Icon={TimerOutlined} metricTitle="Your QPH" metricValue={qphValue || "- -"} />;
};

interface MetricDisplayProps {
  Icon: SvgIconComponent;
  metricTitle: string;
  metricValue: string | number;
}

export const MetricDisplay = ({ Icon, metricTitle, metricValue }: MetricDisplayProps) => {
  return (
    <MetricContainer>
      <MetricIconBox>
        <Icon fontSize="large" />
      </MetricIconBox>
      <MetricInfoBox>
        <MetricCaption>{metricTitle}</MetricCaption>
        <BoldMetric>{metricValue}</BoldMetric>
      </MetricInfoBox>
    </MetricContainer>
  );
};

const BoldMetric = styled(H1)({
  fontWeight: 500,
  fontSize: 48,
});

const MetricCaption = styled(Caption)(({ theme }) => ({
  color: colorsLight.font.light,
  marginBottom: theme.spacing(1),
}));

const MetricContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
});

const MetricInfoBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
});

const MetricIconBox = styled(Card)(({ theme }) => ({
  width: 64,
  height: 64,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: colorsLight.primary.contrastText,
  marginRight: theme.spacing(2),
  borderRadius: "10px",
  color: colorsLight.primary.dark,
  border: `1px solid ${colorsLight.primary.faint10}`,
}));

export default DashboardMetrics;
